import type { Disclosure } from '@/components/common/helpers';
import { UserMultiSelect } from '@/components/common/input/users';
import { BasicModal, BasicModalFooter } from '@/components/common/modal';
import { Debug } from '@/components/common/output/debug';
import { useApiWithAuth } from '@/hooks/api';
import type { Campus, CampusRoleKey } from '@/shared/types/campus';
import { useOrgStore } from '@/store/org';
import { updateCampus } from '@/store/org/actions';
import {
  AllCampusRoleKeys,
  getOrgAlias,
  getOrgRoleLabel,
} from '@/store/org/types';
import { Input, ModalBody, ModalHeader } from '@/components/ui';
import { useEffect, useState } from 'react';

export function CampusModal({
  disclosure,
  item,
}: {
  disclosure: Disclosure;
  item: Campus;
}) {
  const store = useOrgStore();
  const { api, auth } = useApiWithAuth();
  const label = getOrgAlias('campus', 'type');
  const ready = Boolean(item);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState(
    item || ({ id: '', data: {} } as Campus),
  );
  useEffect(() => {
    if (!disclosure.isOpen) {
      return;
    }

    const value = item || ({ id: '', data: {} } as Campus);
    console.debug('setting editable:', JSON.stringify(value));
    setEditable(value);
  }, [item, disclosure.isOpen]);
  const updateEditable = (data: Partial<Campus['data']>) => {
    const updated: Campus = {
      id: editable.id || item?.id || '',
      data: {
        ...item?.data,
        ...editable.data,
        ...data,
      },
    };
    setEditable(updated);
  };

  const onNameChange = (value: string) => {
    updateEditable({
      name: value,
    });
  };

  const onRoleChange = (role: CampusRoleKey, ids: string[]) => {
    updateEditable({
      roles: {
        ...(editable.data.roles || {}),
        [role]: ids,
      },
    });
  };

  const onSave = async () => {
    setSaving(true);
    await updateCampus(store, { api, auth, item: editable });
    setSaving(false);
    disclosure.onClose();
  };

  return (
    <BasicModal disclosure={disclosure} ready={ready}>
      <ModalHeader>Edit {label}</ModalHeader>
      <ModalBody>
        <Input
          label='Name'
          defaultValue={item?.data.name || ''}
          onValueChange={onNameChange}
        />
        {AllCampusRoleKeys.map((role) => (
          <UserMultiSelect
            key={role}
            label={getOrgRoleLabel('campus', role, true)}
            roles={['people', 'admin']}
            status='unarchived'
            initialSelected={item.data.roles?.[role]}
            onChange={(ids: string[]) => onRoleChange(role, ids)}
          />
        ))}
        <Debug value={editable} />
      </ModalBody>
      <BasicModalFooter
        onCancel={disclosure.onClose}
        onApply={onSave}
        applyLabel='Update'
        cancelDisabled={saving}
        applyLoading={saving}
      />
    </BasicModal>
  );
}
